import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import hrApi from "./hrApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2 } from "lucide-react";

export default function HrLeave() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ type: "", start_date: "", end_date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    hrApi.get(`/public/link/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(e.response?.data?.detail || "This link is not valid."));
  }, [token]);

  const submit = async () => {
    if (!f.type) return toast.error("Choose a leave type");
    if (!f.start_date) return toast.error("Choose a start date");
    setBusy(true);
    try { await hrApi.post(`/public/leave/${token}/request`, f); setDone(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    setBusy(false);
  };

  if (err) return <Shell><div className="text-center text-slate-500 py-6" data-testid="leave-invalid">{err}</div></Shell>;
  if (!info) return <Shell><div className="text-center text-slate-400 py-6">Loading…</div></Shell>;
  if (done) return <Shell><div className="text-center space-y-3 py-6" data-testid="leave-done">
    <div className="w-16 h-16 rounded-full bg-teal-100 grid place-items-center mx-auto"><CheckCircle2 className="text-teal-600" size={32} /></div>
    <h1 className="font-heading font-extrabold text-2xl text-slate-800">Request sent</h1>
    <p className="text-slate-500">Thanks — HR will review your leave request.</p>
  </div></Shell>;

  return (
    <Shell>
      <div data-testid="leave-request-page" className="space-y-5">
        <div className="flex items-center gap-2"><CalendarDays className="text-indigo-500" size={22} /><h1 className="font-heading font-extrabold text-2xl text-slate-800">Request leave</h1></div>
        <p className="text-slate-500 text-sm -mt-3">Hi {info.preferred_name || info.employee_name}, fill in your leave request below.</p>
        <div><Label className="text-sm font-medium text-slate-600">Leave type *</Label>
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="lr-type" className="h-12 mt-1.5 text-base rounded-xl"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>{(info.leave_types || []).map((t) => <SelectItem key={t} value={t} className="text-base py-2.5">{t}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-sm font-medium text-slate-600">Start date *</Label><Input data-testid="lr-start" type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="h-12 mt-1.5 text-base rounded-xl" /></div>
        <div><Label className="text-sm font-medium text-slate-600">End date</Label><Input data-testid="lr-end" type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="h-12 mt-1.5 text-base rounded-xl" /></div>
        <div><Label className="text-sm font-medium text-slate-600">Reason (optional)</Label><Textarea data-testid="lr-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="mt-1.5 text-base" /></div>
        <Button data-testid="lr-submit" onClick={submit} disabled={busy} className="w-full h-14 bg-indigo-500 hover:bg-indigo-400 text-white rounded-2xl font-bold text-base">{busy ? "Sending…" : "Submit request"}</Button>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-slate-100 py-6 px-4"><div className="max-w-md mx-auto bg-white rounded-3xl shadow-sm p-5 sm:p-6">{children}</div></div>;
}
